import { api } from "@/modules/api";
import { getBackendApiErrorMessage } from "@/modules/api/errorHandling";
import { AppStore } from "@/store";
import { resolveNoteSuggestionBaseContent } from "@/store/note/helpers/note-suggestion-content";
import { runInAction } from "mobx";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { noteModule } from "@mem-labs/common-editor";
import { UpsertNoteSuggestionOperation } from "@/store/sync/operations/notes/UpsertNoteSuggestionOperation";
import { uuidModule } from "@/modules/uuid";

export const cleanupNote = async ({ noteId, store }: { noteId: string; store: AppStore }) => {
  const startTime = Date.now();

  try {
    const note = await store.notes.getAsync(noteId);
    if (!note) return { success: false, error: "Note not found" };
    runInAction(() => (note.isProcessingSuggestion = true));

    try {
      const {
        encodedContent: prevEncodedContent,
        markdownContent: prevMarkdownContent,
        primaryLabel: prevPrimaryLabel,
        secondaryLabel: prevSecondaryLabel,
      } = await resolveNoteSuggestionBaseContent(note);
      const sessionId = uuidModule.generate();

      const response = await api.post("/v2/notes/{note_id}/cleanup/process", {
        params: { path: { note_id: noteId } },
        body: {
          associated_session_id: sessionId,
          associated_note_markdown_content: prevMarkdownContent,
        },
      });
      if (!response.response.ok) {
        const errorMessage = getBackendApiErrorMessage(response.error) ?? "Unknown error";
        throw new Error(errorMessage);
      }

      const suggestedMarkdownContent = response.data?.suggested_markdown_content;
      if (!suggestedMarkdownContent) {
        throw new Error("No suggested content returned from server");
      }

      const {
        suggestedEncodedContent,
        suggestedEncodedContentDiff,
        suggestedPrimaryLabel,
        suggestedSecondaryLabel,
      } = noteModule.buildSuggestionData({
        previousEncodedContent: prevEncodedContent,
        suggestedMarkdownContent,
      });
      const suggestionId = uuidModule.generate();

      await new UpsertNoteSuggestionOperation({
        store,
        payload: {
          suggestion_id: suggestionId,
          note_id: noteId,
          kind: "NOTE_CLEANUP_V1",
          prev_encoded_content: prevEncodedContent,
          prev_primary_label: prevPrimaryLabel,
          prev_secondary_label: prevSecondaryLabel,
          prev_markdown_content: prevMarkdownContent,
          suggested_encoded_content: suggestedEncodedContent,
          suggested_encoded_content_diff: suggestedEncodedContentDiff,
          suggested_primary_label: suggestedPrimaryLabel,
          suggested_secondary_label: suggestedSecondaryLabel,
          suggested_markdown_content: suggestedMarkdownContent,
          metadata: {
            session_id: sessionId,
          },
        },
      }).execute();

      const endTime = Date.now();
      const elapsedTime = Math.floor((endTime - startTime) / 1000);

      trackEvent(TrackedEvent.NoteCleanupStart, {
        session_id: sessionId,
        note_id: noteId,
        elapsed_time: elapsedTime,
        previous_title: prevPrimaryLabel,
        previous_markdown_contents: prevMarkdownContent,
        suggested_markdown_contents: suggestedMarkdownContent,
        suggested_title: suggestedPrimaryLabel,
      });

      return { success: true };
    } finally {
      runInAction(() => (note.isProcessingSuggestion = false));
    }
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
  }
};
